import { useState } from "react";
import type { MessageDto } from "@multi-chat/shared";
import { unreadSeparatorAnchor, type UnreadSeparator } from "../conversation-unread-separator.js";

interface OpenedConversation {
  conversationId: number | null;
  unreadCount: number;
  /** Frozen once the first page is ready, so new arrivals and mark-read never move it. */
  separator: UnreadSeparator | null;
}

const NO_SEPARATOR: UnreadSeparator = { kind: "none" };

/** "New messages" placement for the unread count the conversation had when it was opened. */
export function useUnreadSeparator({
  conversationId,
  unreadCount,
  messages,
}: {
  conversationId: number | null;
  unreadCount: number;
  /** Null until the conversation's messages are ready. */
  messages: readonly MessageDto[] | null;
}): UnreadSeparator {
  const [opened, setOpened] = useState<OpenedConversation>(() => ({
    conversationId,
    unreadCount,
    separator: null,
  }));

  let current = opened;
  if (current.conversationId !== conversationId) {
    current = { conversationId, unreadCount, separator: null };
    setOpened(current);
  }
  if (current.separator == null && messages != null) {
    current = { ...current, separator: unreadSeparatorAnchor(messages, current.unreadCount) };
    setOpened(current);
  }

  return current.separator ?? NO_SEPARATOR;
}
